<template>
  <div>
    <DashPeriodFilter
      :period="filters.period"
      :from="filters.from"
      :to="filters.to"
      :applied="appliedPeriod"
      :loading="loading"
      @change="onFilterChange"
    />

    <div v-if="loading && !loaded" class="text-center py-6 text-muted">
      <div class="spinner-border text-primary mb-3" role="status"></div>
      <p class="mb-0">Carregando...</p>
    </div>

    <div v-else-if="errored" class="text-center py-6 text-muted">
      <i class="fa-solid fa-triangle-exclamation fa-2x mb-3"></i>
      <p class="mb-2">{{ errorMessage || "Não foi possível carregar os dados." }}</p>
      <button class="btn btn-sm btn-outline-primary" @click="fetch">Tentar novamente</button>
    </div>

    <div v-else class="row g-3">
      <div class="col-12">
        <WidgetCard title="Receita de Assinaturas Mensal (MRR)">
          <p class="text-muted small mb-2 px-2">
            Assinaturas anuais e semestrais são mensalizadas (valor total
            dividido pelos meses do plano).
          </p>
          <ChartLine :data="mrr" :options="moneyOptions" />
        </WidgetCard>
      </div>
      <div class="col-12">
        <WidgetCard title="Faturamento de Consultas">
          <p class="text-muted small mb-2 px-2">
            Soma do valor bruto da sessão na Stripe, na data do pagamento
            (data de criação da sessão).
          </p>
          <ChartLine :data="revenueTimeline" :options="moneyOptions" />
        </WidgetCard>
      </div>
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h6 class="mb-0">Extrato Stripe — dinheiro real</h6>
            <div class="btn-group btn-group-sm" role="group">
              <button
                v-for="g in granularities"
                :key="g.id"
                type="button"
                class="btn"
                :class="stripeGranularity === g.id ? 'btn-primary' : 'btn-outline-secondary'"
                @click="stripeGranularity = g.id"
              >
                {{ g.label }}
              </button>
            </div>
          </div>
          <div class="card-body">
            <p class="text-muted small mb-2">
              Diferente do gráfico de faturamento acima (que usa nosso cadastro
              de preços), este extrato vem direto da Stripe: soma tudo que
              entrou (sessões, assinaturas, taxas de entrada) e o que
              efetivamente ficou pra plataforma depois de repasses, taxas da
              Stripe e estornos.
            </p>
            <p v-if="syncedUntilLabel" class="text-muted small mb-2">
              <i class="fa-solid fa-circle-info me-1"></i>
              Extrato do Stripe sincronizado até {{ syncedUntilLabel }}. Períodos
              depois disso ainda não têm dado importado (aparecem como um corte
              no gráfico, não como zero real).
            </p>
            <p v-if="stripeGranularity === 'hourly'" class="text-muted small mb-2">
              <i class="fa-solid fa-clock me-1"></i>
              A visão Horário sempre mostra as últimas 72 horas, independente do
              período selecionado acima.
            </p>
            <div class="widget-chart" style="height: 300px; position: relative">
              <ChartLine :data="stripeSeries" :options="stripeMoneyOptions" />
            </div>
            <ul class="stripe-footnotes text-muted small mb-0 mt-3">
              <li>
                <strong>* Faturamento</strong> — soma de tudo que entrou na
                Stripe: cobranças de sessões pelo valor cheio, assinaturas
                Psiapp+ e taxas de entrada.
              </li>
              <li>
                <strong>* Receita líquida da plataforma</strong> — comissão das
                sessões + assinaturas + taxas de entrada, menos repasse aos
                psicólogos, taxas da Stripe e estornos. É o que efetivamente
                fica no Psiapp. Não desconta custos da empresa (salário,
                infraestrutura, imposto) — não é lucro contábil.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { COLORS, moneyBR, baseOptions, dateTicks } from "@/utils/dashboardCharts";
import WidgetCard from "./WidgetCard.vue";
import DashPeriodFilter from "./DashPeriodFilter.vue";

const PERIOD_FORMATS = {
  monthly: "YYYY-MM",
  daily: "YYYY-MM-DD",
  hourly: "YYYY-MM-DD HH:mm",
};

export default {
  name: "DashFinancial",
  components: { WidgetCard, DashPeriodFilter },
  data() {
    return {
      loading: false,
      loaded: false,
      errored: false,
      errorMessage: "",
      filters: { period: "last_12_months", from: "", to: "" },
      appliedPeriod: null,
      data: {},
      stripeGranularity: "monthly",
      granularities: [
        { id: "monthly", label: "Mensal" },
        { id: "daily", label: "Diário" },
        { id: "hourly", label: "Horário" },
      ],
      moneyOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ ticks: dateTicks }],
          yAxes: [{ ticks: { beginAtZero: true, callback: (v) => moneyBR(v) } }],
        },
        tooltips: {
          callbacks: {
            label: (item, d) => {
              const ds = d.datasets[item.datasetIndex];
              return `${ds.label}: ${moneyBR(item.yLabel)}`;
            },
          },
        },
      },
      // Receita líquida pode ser negativa (estornos > cobranças no período) —
      // não pode travar o eixo Y em zero, senão o gráfico mente.
      stripeMoneyOptions: {
        ...baseOptions,
        scales: {
          xAxes: [{ ticks: dateTicks }],
          yAxes: [{ ticks: { callback: (v) => moneyBR(v) } }],
        },
        tooltips: {
          callbacks: {
            label: (item, d) => {
              const ds = d.datasets[item.datasetIndex];
              return `${ds.label}: ${moneyBR(item.yLabel)}`;
            },
          },
        },
      },
    };
  },
  computed: {
    mrr() {
      const rows = this.data.mrr_timeline || [];
      return {
        labels: rows.map((r) => r.year_month),
        datasets: [
          {
            label: "MRR ativo",
            data: rows.map((r) => Number(r.mrr_active)),
            borderColor: COLORS[1],
            backgroundColor: COLORS[1],
            pointBackgroundColor: "#fff",
            pointRadius: 3,
            fill: false,
          },
        ],
      };
    },
    // `profit` (20% fixo sobre o valor da consulta) saiu do payload — o número
    // real de margem está no bloco `stripe` (net_revenue) mais abaixo.
    revenueTimeline() {
      const rows = this.data.appointments_revenue_timeline || [];
      return {
        labels: rows.map((r) => r.year_month),
        datasets: [
          {
            label: "Faturamento (volume)",
            data: rows.map((r) => Number(r.revenue)),
            borderColor: COLORS[1],
            backgroundColor: COLORS[1],
            pointBackgroundColor: "#fff",
            pointRadius: 3,
            fill: false,
          },
        ],
      };
    },
    syncedUntil() {
      return this.data.stripe?.synced_until || null;
    },
    syncedUntilLabel() {
      return this.syncedUntil ? this.$moment(this.syncedUntil).format("DD/MM/YYYY HH:mm") : null;
    },
    stripeSeries() {
      const rows = this.data.stripe?.[this.stripeGranularity] || [];
      const format = PERIOD_FORMATS[this.stripeGranularity];
      const syncMoment = this.syncedUntil ? this.$moment(this.syncedUntil) : null;

      // Balde depois do synced_until não tem dado importado ainda — manda
      // null (não 0) pra virar um corte no gráfico, não uma queda falsa.
      const valueOrNull = (row, key) => {
        if (syncMoment && this.$moment(row.period, format).isAfter(syncMoment)) return null;
        return Number(row[key]);
      };
      // `profit` foi depreciado a favor de `net_revenue` — usa o novo campo e
      // cai pro antigo enquanto os dois convivem no payload.
      const netRevenueOf = (row) => (row.net_revenue !== undefined ? row.net_revenue : row.profit);

      return {
        labels: rows.map((r) => r.period),
        datasets: [
          {
            label: "Faturamento*",
            data: rows.map((r) => valueOrNull(r, "revenue")),
            borderColor: COLORS[1],
            backgroundColor: COLORS[1],
            pointBackgroundColor: "#fff",
            pointRadius: 2,
            fill: false,
            spanGaps: false,
          },
          {
            label: "Taxa Stripe",
            data: rows.map((r) => valueOrNull(r, "stripe_fee")),
            borderColor: COLORS[4],
            backgroundColor: COLORS[4],
            pointBackgroundColor: "#fff",
            pointRadius: 2,
            fill: false,
            spanGaps: false,
          },
          {
            label: "Receita líquida da plataforma*",
            data: rows.map((r) => {
              if (syncMoment && this.$moment(r.period, format).isAfter(syncMoment)) return null;
              return Number(netRevenueOf(r));
            }),
            borderColor: COLORS[3],
            backgroundColor: COLORS[3],
            pointBackgroundColor: "#fff",
            pointRadius: 2,
            fill: false,
            spanGaps: false,
          },
        ],
      };
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    onFilterChange(f) {
      this.filters = f;
      this.fetch();
    },
    async fetch() {
      this.loading = true;
      this.errored = false;
      try {
        const res = await this.$dashboardService.financial(this.filters);
        this.data = res.data || {};
        this.appliedPeriod = res.period || null;
        this.loaded = true;
      } catch (e) {
        this.errored = true;
        this.errorMessage = e.message;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.stripe-footnotes {
  list-style: none;
  padding-left: 0;
}
.stripe-footnotes li {
  border-left: 3px solid #e3e8ef;
  padding-left: 10px;
  margin-bottom: 6px;
}
.stripe-footnotes li:last-child {
  margin-bottom: 0;
}
</style>
